<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { HOME_BY_ROLE } from './router.js';
import { useAuthStore } from './stores/auth.js';
import { useThemeStore } from './stores/theme.js';
import { useSettingsStore } from './stores/settings.js';
import { flush } from './api/queue.js';
import { api } from './api/http.js';
import StatusStrip from './components/StatusStrip.vue';
import ThemeToggle from './components/ThemeToggle.vue';
import LocaleToggle from './components/LocaleToggle.vue';
import { useI18n } from './i18n/index.js';
import { useMetaStore } from './stores/meta.js';
import ConfirmHost from './components/ConfirmHost.vue';
import AppIcon from './components/AppIcon.vue';
import ShiftMeter from './components/ShiftMeter.vue';
import { on, EVENTS } from './api/socket.js';
import { shortEquipmentName } from './composables/naming.js';
import { useOffice } from './i18n/office.js';
import ReadOnlyBanner from './components/ReadOnlyBanner.vue';

const { to } = useOffice();

const auth = useAuthStore();
/* Навигацию видит оператор каждую смену — она переводится вместе
   с цеховыми экранами. */
const { t, locale } = useI18n();
const meta = useMetaStore();
const theme = useThemeStore();
const settings = useSettingsStore();
const router = useRouter();
const route = useRoute();

useThemeStore();   // применяет тему при старте

/* Названия ролей берём у общего справочника: он же приходит с сервера
   и он же переводится. Локальная копия здесь однажды разошлась бы
   с остальным интерфейсом — и первым делом при смене языка. */

const isPublic = computed(() => route.meta.public === true);
/* Экран без оболочки: публичные страницы и цеховое табло. У табло нет
   пользователя, который ходил бы по разделам, а панель навигации на стене
   отнимает место у плиток. */
const isBare = computed(() => isPublic.value || route.meta.fullscreen === true);

/* Навигация вертикальная слева: она освобождает высоту, а на планшете
   в альбомной ориентации высота дороже ширины. Подписи под иконками
   оставлены намеренно — в цехе не запоминают пиктограммы, а ошибка
   в разделе стоит дороже сэкономленных пикселей. */
/* Навигация группами.

   Четырнадцать пунктов подряд перестали читаться и начали прокручиваться:
   мастер листал меню, чтобы найти раздел. Группы это чинят, но НЕ становятся
   меню — они всегда раскрыты, и ни одного нажатия не добавляют. Свернуть их
   значило бы обменять прокрутку на клик, то есть починить одно неудобство
   другим.

   Группа — это вопрос, с которым человек пришёл, а не тип данных. «Металл» —
   потому что материалы, партии и лом это один и тот же металл на разных
   стадиях; складывать их по признаку «справочник, экран, отчёт» значит
   раскладывать по нашему устройству, а не по его работе.

   Заголовок появляется только там, где групп больше одной. У оператора
   их две строки в одной группе, и подпись над ними была бы шумом. */
const NAV_GROUPS = [
  {
    key: 'shop',
    label: 'nav.groupShop',
    items: [
      { to: '/operator', label: 'nav.operator', short: 'nav.operatorShort', icon: 'workplace', roles: ['operator', 'supervisor', 'admin'] },
      { to: '/melt', label: 'nav.melt', icon: 'melt', roles: ['operator', 'supervisor', 'manager', 'admin'] },
      { to: '/supervisor', label: 'nav.area', icon: 'area', roles: ['supervisor', 'manager', 'admin'] },
      { to: '/maintenance', label: 'nav.maintenance', short: 'nav.maintenanceShort', icon: 'maintenance', roles: ['setter', 'supervisor', 'manager', 'admin'] },
    ],
  },
  {
    key: 'quality',
    label: 'nav.groupQuality',
    items: [
      { to: '/quality', label: 'nav.quality', short: 'nav.qualityShort', icon: 'quality', roles: ['qc', 'supervisor', 'manager', 'admin'] },
      { to: '/lab', label: 'nav.lab', short: 'nav.labShort', icon: 'lab', roles: ['qc', 'supervisor', 'manager', 'admin'] },
      { to: '/certificates', label: 'nav.certificates', short: 'nav.certificatesShort', icon: 'certificate', roles: ['qc', 'supervisor', 'manager', 'admin'] },
    ],
  },
  {
    key: 'metal',
    label: 'nav.groupMetal',
    items: [
      { to: '/stock', label: 'nav.stock', short: 'nav.stockShort', icon: 'materials', roles: ['supervisor', 'manager', 'admin'] },
      { to: '/batches', label: 'nav.batches', icon: 'batches', roles: ['supervisor', 'manager', 'admin', 'qc'] },
      { to: '/scrap', label: 'nav.scrap', icon: 'scrap', roles: ['supervisor', 'manager', 'admin'] },
    ],
  },
  {
    key: 'plan',
    label: 'nav.groupPlans',
    items: [
      { to: '/orders', label: 'nav.orders', icon: 'orders', roles: ['planner', 'manager', 'admin'] },
      { to: '/shift', label: 'nav.shift', icon: 'shift', roles: ['supervisor', 'manager', 'admin'] },
      { to: '/manager', label: 'nav.production', short: 'nav.productionShort', icon: 'production', roles: ['manager', 'admin', 'supervisor', 'planner'] },
    ],
  },
];

const nav = computed(() => NAV_GROUPS
  .map((g) => ({ ...g, items: g.items.filter((n) => n.roles.includes(auth.role)) }))
  .filter((g) => g.items.length));

/* Подписи нужны, только когда есть что различать. Одна группа — значит
   различать нечего, и подпись над ней ничего не сообщает. */
const навПодписи = computed(() => nav.value.filter((g) => g.label).length > 1);

const initials = computed(() => (auth.user?.name || '')
  .split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase());

/* Станок в шапке. Код остаётся всегда, повтор вырезается из названия —
   а не наоборот: на узком экране название скрыто медиазапросом, и если
   прятать код, от станка не остаётся ничего именно там, где места меньше. */
const place = computed(() => {
  const e = auth.equipment;
  if (!e?.code) return null;
  return { code: e.code, name: shortEquipmentName(e.code, e.name) };
});

async function logout() {
  /* Оператор перед выходом закрывает смену на терминале — из неё считается
     табель. Ошибку глотаем намеренно: если связи нет, смена закроется входом
     следующего или окончанием производственной смены, а держать человека
     у станка из-за неотправленного запроса нельзя. */
  if (auth.terminal && auth.role === 'operator') {
    await api.post('/auth/terminal/logout').catch(() => {});
  }
  auth.logout();
  router.push(auth.terminal ? '/terminal' : '/login');
}

/* Открытые вызовы с рабочих мест.

   Показываем в шапке, а не только на экране участка: оператор жмёт «вызов
   мастера», когда встал станок, а мастер в этот момент может смотреть заказы
   или закрывать смену. Вызов, который видно на одном экране, — это вызов,
   который заметят через полчаса. */
const CAN_SEE_CALLS = ['supervisor', 'manager', 'admin'];
const canSeeCalls = computed(() => CAN_SEE_CALLS.includes(auth.role));
const calls = ref(0);

async function loadCalls() {
  if (!canSeeCalls.value) { calls.value = 0; return; }
  try { calls.value = (await api.get('/production/calls')).items?.length || 0; }
  catch { /* счётчик в шапке не имеет права ронять экран */ }
}

let shiftTimer = null;
let offAlert = null;

onMounted(() => {
  // Незавершённая очередь с прошлой сессии отправляется сразу при запуске
  void flush();

  // Вызов появляется и снимается событием — счётчик не должен ждать перезагрузки
  offAlert = on(EVENTS.alert, () => { void loadCalls(); });

  /* Планшет у станка работает сутками. Раз в минуту сверяем, не кончилась ли
     смена: если кончилась — перечитываем с сервера. Иначе экран до утра
     показывал бы смену, законченную вечером. */
  shiftTimer = setInterval(() => {
    const to = auth.shift?.to ? Date.parse(auth.shift.to) : null;
    if (to && Date.now() >= to) void auth.refreshShift();
  }, 60000);
});

onUnmounted(() => {
  if (shiftTimer) clearInterval(shiftTimer);
  offAlert?.();
});

/* Настройки грузим по факту авторизации, а не в onMounted: приложение
   монтируется раньше, чем роутер успевает восстановить сессию, и одноразовый
   вызов в onMounted промахивался бы при перезагрузке страницы. */
watch(() => auth.isAuthenticated, (yes) => {
  if (yes) { void settings.load(); void loadCalls(); }
  else calls.value = 0;
}, { immediate: true });

/* Сессия кончилась — уводим на вход.

   Проверка прав живёт в маршрутизаторе, а он срабатывает только при переходе.
   Пока человек стоит на одном экране, перехода нет: истёкший токен гасил
   сессию, панель и шапка исчезали, а сам экран оставался — со старыми
   цифрами, которые больше не обновляются. Хуже пустого экрана: выглядит
   рабочим. Поэтому уходим сами.

   Оператора ведём на экран терминала, остальных — на форму входа: пароля
   оператор не знает, и общая форма для него тупик. */
watch(() => auth.isAuthenticated, (yes, было) => {
  if (yes || !было) return;
  if (route.meta.public) return;
  router.push(auth.terminal ? '/terminal' : '/login');
});

/* Сменился человек, а не сессия — вошли другим в соседней вкладке.

   Проверка роли тоже висит на переходе, поэтому планировщик оставался
   стоять на экране мастера: в меню такого раздела уже нет, а сам экран
   никуда не делся. Уводим на его собственную стартовую страницу. */
watch(() => auth.role, (нов, стар) => {
  if (!нов || !стар || нов === стар) return;
  const можно = route.meta.roles;
  if (можно && !можно.includes(нов)) router.push(HOME_BY_ROLE[нов] || '/');
});
</script>

<template>
  <div class="shell" :class="{ plain: isBare || !auth.isAuthenticated }">
    <aside v-if="!isBare && auth.isAuthenticated" class="rail glass-shell">
      <div class="brand">
        <span class="mark">IOTICS</span>
        <span class="sub">MES</span>
      </div>

      <nav class="nav">
        <!-- Две подписи вместо одной: на узкой панели длинная обрезалась бы
             многоточием, а обрезанная подпись хуже короткой -->
        <div v-for="g in nav" :key="g.key" class="nav-group">
          <!-- Подпись группы — не кнопка: группы всегда раскрыты, и свернуть
               их значило бы обменять прокрутку на нажатие. На узкой панели
               подпись превращается в разделительную черту: текст в 10px над
               каждыми тремя пунктами читается как шум. -->
          <div v-if="навПодписи && g.label" class="nav-cap">
            <span class="nav-cap-txt">{{ t(g.label) }}</span>
          </div>
          <RouterLink v-for="n in g.items" :key="n.to" :to="n.to" class="nav-link" :title="t(n.label)">
            <AppIcon :name="n.icon" />
            <span class="nav-lbl full">{{ t(n.label) }}</span>
            <span class="nav-lbl short">{{ t(n.short || n.label) }}</span>
          </RouterLink>
        </div>
      </nav>

      <span class="spacer" />

      <!-- Настройка живёт здесь, а не в списке разделов: это конфигурация
           системы, а не рабочее место. Рядом тема и выход — всё, что про
           систему, а не про работу. -->
      <RouterLink v-if="auth.role === 'admin'" to="/admin" class="rail-btn setup-link" :title="to('Настройка')">
        <AppIcon name="settings" :size="18" />
        <span class="lbl">{{ to('Настройка') }}</span>
      </RouterLink>

      <LocaleToggle class="rail-btn" />

      <button class="who" :title="to('{n1} — выйти', { n1: auth.user.name })" @click="logout">
        <span class="ava">{{ initials }}</span>
        <span class="who-txt">
          <strong>{{ auth.user.name }}</strong>
          <span class="dim">{{ meta.roleName(auth.role) || auth.role }}</span>
        </span>
        <AppIcon name="logout" :size="18" class="out" />
      </button>
    </aside>

    <div class="stack">
      <template v-if="!isBare && auth.isAuthenticated">
        <!-- Полоса контекста: где я, когда я, всё ли в порядке со связью
             и не зовёт ли кто-то из цеха. Раньше здесь было название
             предприятия, полоса связи и код смены — на две трети пустая
             строка, в которой единственное число дублировалось подзаголовком
             экрана прямо под ней. -->
        <header class="ctxbar glass-shell">
          <div class="where">
            <span class="company">{{ settings.companyName || t('nav.company') }}</span>
            <!-- Станок оператора — часть ответа на вопрос «где я»:
                 отчёт уйдёт именно на него, и ошибиться тут дорого -->
            <span v-if="place" class="place">
              <span v-if="place.code" class="mono">{{ place.code }}</span>
              <span v-if="place.name" class="place-name">{{ place.name }}</span>
            </span>
          </div>

          <span class="spacer" />

          <!-- Вызов с рабочего места. Кнопка, а не значок: увидел — нажал
               и попал на участок, где видно, кто и откуда зовёт -->
          <RouterLink v-if="canSeeCalls && calls" to="/supervisor" class="calls" :title="to('Открытые вызовы с рабочих мест')">
            <AppIcon name="bell" :size="16" />
            <span>{{ calls }}</span>
          </RouterLink>

          <StatusStrip />

          <span class="divider" />

          <ShiftMeter />
        </header>
      </template>

      <main class="body" :class="{ plain: isBare, full: route.meta.fullscreen === true }">
        <!-- Ключ по языку: экраны кабинета собирают часть подписей в обычные
             массивы при монтировании, а не в computed. Без перемонтирования
             такие подписи остались бы на прежнем языке до перезагрузки. -->
        <!-- Полоса «телефон — только просмотр» — одна на всё приложение.
             Раньше её ставил каждый экран сам, и поставили три из четырнадцати:
             на остальных кнопки изменений с телефона выглядели рабочими.
             Запрет самих изменений — в api/http.js; полоса объясняет, почему
             нажатие не проходит, до того как человек нажмёт. -->
        <div v-if="!isBare && auth.isAuthenticated" class="ro-global"><ReadOnlyBanner /></div>
        <RouterView :key="locale" />
      </main>
    </div>

    <!-- Подтверждения всего приложения рисует один диалог. Заводить его копию
         на каждом экране значит однажды получить четыре разных подтверждения
         одного и того же вида. -->
    <ConfirmHost />
  </div>
</template>

<style scoped>
/* Отступ оболочки вынесен в переменную: от него считается высота панели,
   и на узком экране, где отступ меньше, они не должны разъезжаться */
/* Оболочка ровно по экрану, а не по содержимому.

   Было min-height: 100%, и страница росла вместе с рабочей областью:
   на терминале это значило, что до нижней кнопки — «Вызвать наладчика» —
   надо доскроллить. У станка это худший вид неудобства: человек в перчатках
   тянется к кнопке, которой не видно.

   Теперь прокручивается только рабочая область, а шапка со сменой и связью
   и левая панель остаются на месте — их и надо видеть всегда. */
.shell { --pad: var(--gap); display: flex; align-items: stretch; gap: var(--gap);
  height: 100%; padding: var(--pad); }
.shell.plain { --pad: 0px; gap: 0; }

/* min-height: 0 обязателен: без него колоночный флекс не даёт детям
   ужаться ниже их содержимого, и .body растягивает страницу вместо того,
   чтобы прокрутиться внутри. */
.stack { flex: 1; display: flex; flex-direction: column; gap: var(--gap);
  min-width: 0; min-height: 0; }

/* ------------------------------- левая панель ------------------------------- */

/* Панель во всю высоту экрана, а не по высоте своего содержимого.

   Раньше она обрывалась под учётной записью, и ниже оставался голый фон:
   восемь пунктов меню не заполняют экран, и панель читалась как случайно
   зависшая карточка, а не как край окна. Высота фиксирована по экрану,
   а не растянута по документу: `align-items: stretch` тянул бы её на всю
   длину прокручиваемой страницы, и `sticky` перестал бы работать. */
.rail {
  position: sticky; top: var(--pad);
  align-self: flex-start;
  display: flex; flex-direction: column; gap: 6px;
  width: 208px; flex: none;
  height: calc(100vh - var(--pad) * 2);
  padding: 18px 12px 12px;
}
/* dvh — про адресную строку на планшете: она то появляется, то исчезает,
   и с vh панель на эту высоту вылезала бы за экран.

   Через @supports, а не вторым объявлением следом. Второе объявление здесь
   запасом не работает: внутри calc стоит var(--pad), поэтому старый браузер
   принимает строку при разборе и отбрасывает уже при вычислении - высота
   сбрасывается в auto, а не откатывается к строке с vh. Проверено. */
@supports (height: 100dvh) {
  .rail { height: calc(100dvh - var(--pad) * 2); }
}

.brand { display: flex; align-items: baseline; gap: 6px; padding: 0 10px 14px; }
/* Логотип берёт акцент, а не --gold напрямую: яркое золото на светлой теме
   даёт 1.96, то есть надпись почти исчезает. В тёмной теме акцент и есть золото. */
.mark { font-weight: 700; letter-spacing: .08em; color: var(--accent); }
.sub { font-size: var(--fs-label); font-weight: 600; color: var(--t3); letter-spacing: .1em; }

/* Меню прокручивается внутри панели, когда пункты не помещаются в экран
   (низкое разрешение планшета, узкий режим). Полоса прокрутки тонкая и
   бесцветная: системная в полтора десятка пикселей режет подписи и спорит
   со стеклом, а нужна она здесь редко. */
.nav-group { display: flex; flex-direction: column; gap: 4px; }
.nav-cap { padding: 8px 12px 2px; }
.nav-group:first-child .nav-cap { padding-top: 0; }
.nav-cap-txt { font-size: var(--fs-label); color: var(--t3); letter-spacing: .04em; white-space: nowrap; }

.nav { display: flex; flex-direction: column; gap: 4px; min-height: 0; overflow-y: auto;
  scrollbar-width: thin; scrollbar-color: var(--border-strong) transparent; }
.nav::-webkit-scrollbar { width: 6px; }
.nav::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 3px; }
.nav::-webkit-scrollbar-track { background: transparent; }
.nav-link {
  display: flex; align-items: center; gap: 12px;
  min-height: 40px; padding: 0 12px;
  border-radius: var(--r-md);
  color: var(--t2); font-weight: 500;
  transition: background var(--transition), color var(--transition);
}
.nav-link:hover { background: var(--surface-2); color: var(--t1); }
.nav-link.router-link-active {
  background: color-mix(in srgb, var(--accent) 14%, transparent);
  color: var(--accent); font-weight: 600;
}
.nav-lbl { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nav-lbl.short { display: none; }

/* Кнопки низа панели той же высоты, что пункты списка: рядом стоящие
   элементы разной высоты читаются как разные по важности, а они одинаковы.
   На планшетной раскладке ниже они снова становятся крупными — там касаются. */
.rail-btn { width: 100%; justify-content: flex-start; gap: 12px; min-height: 40px;
  padding: 0 12px; border-radius: var(--r-md); }

/* Учётная запись внизу панели — как аватар в референсе, но с подписью:
   на сменном терминале важно видеть, под кем работаешь */
.who {
  display: flex; align-items: center; gap: 10px;
  min-height: 52px; padding: 0 10px; margin-top: 4px;
  border: 1px solid var(--border); border-radius: var(--r-md);
  background: var(--surface-2); color: var(--t1);
  font: inherit; text-align: left; cursor: pointer;
  transition: background var(--transition);
}
.who:hover { background: var(--surface-hover); }
/* Заливка сплошная, как у круглой акцентной кнопки в референсе: золото
   на своей же прозрачной подложке давало 3.39, а по сплошному золоту
   тёмными буквами контраст с запасом */
.ava {
  display: grid; place-items: center; flex: none;
  width: 32px; height: 32px; border-radius: 50%;
  background: var(--accent-fill); color: var(--accent-contrast);
  font-size: var(--fs-label); font-weight: 700;
}
.who-txt { display: flex; flex-direction: column; line-height: 1.2; min-width: 0; flex: 1; }
.who-txt strong { font-size: 14px; font-weight: 600; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; }
/* Роль стоит на самой светлой поверхности панели — стек стекла здесь
   двойной, и третичный тон на нём даёт 3.91. Берём вторичный. */
.who-txt .dim { font-size: var(--fs-label); color: var(--t2); }
.out { flex: none; color: var(--t3); }

/* ------------------------------- контекст сверху ------------------------------- */

/* Полоса стала поверхностью, а не строчкой на фоне.

   Прозрачная строка с тремя элементами по краям читалась как пустое место
   над содержимым: у неё не было ни границы, ни фона, и глазу не за что
   было её принять. Теперь это стеклянная панель того же материала, что
   и левая колонка, — вместе они обрамляют рабочую область. */
.ctxbar { display: flex; align-items: center; flex-wrap: wrap; gap: 14px;
  padding: 10px 16px; min-height: 56px; }

.where { display: flex; align-items: baseline; gap: 10px; min-width: 0; }
.company { font-size: 15px; font-weight: 600; color: var(--t1); white-space: nowrap; }
/* inline-flex с зазором, а не просто span: два соседних элемента
   слипались в «ТЦ-01Токарный ЧПУ» — перенос строки в шаблоне пробелом
   не становится */
.place { display: inline-flex; align-items: baseline; gap: 7px;
  font-size: 13px; color: var(--t2); white-space: nowrap;
  padding-left: 10px; border-left: 1px solid var(--border); }
.place-name { color: var(--t3); }

/* Разделитель перед сменой: справа стоят два разных по смыслу блока —
   состояние связи и время. Без черты они читаются одной группой. */
.divider { width: 1px; align-self: stretch; margin: 2px 0; background: var(--border); }

/* Вызов из цеха. Красный, потому что это единственный элемент шапки,
   требующий действия: станок стоит и человек ждёт. */
.calls {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 30px; padding: 0 12px 0 10px;
  border-radius: var(--r-pill);
  background: color-mix(in srgb, var(--c-bad) calc(var(--tint-alpha) * 100%), var(--tint-base));
  border: 1px solid color-mix(in srgb, var(--c-bad) 30%, transparent);
  color: var(--c-bad); font-size: var(--fs-label); font-weight: 700;
  transition: background var(--transition);
}
.calls:hover { background: color-mix(in srgb, var(--c-bad) 30%, var(--tint-base)); }

@media (max-width: 900px) {
  .ctxbar { padding: 8px 12px; gap: 10px; min-height: 48px; }
  .place-name { display: none; }
}

.body { flex: 1; min-width: 0; min-height: 0; overflow-y: auto; }
.body.plain { display: grid; place-items: center; }
/* Табло само позиционируется от вьюпорта; здесь только снимаем
   центрирование, чтобы под ним не оставалось пустой прокручиваемой полосы. */
.body.full { display: block; place-items: stretch; }

/* Планшет: панель сжимается до иконок с подписью под ними — подписи важнее
   ширины, но в два уровня они занимают меньше */
@media (max-width: 900px) {
  .rail { width: 76px; padding: 14px 8px 10px; align-items: stretch; }
  .brand { flex-direction: column; align-items: center; gap: 0; padding: 0 0 12px; }
  .sub { letter-spacing: .06em; }
  .mark { font-size: 12px; letter-spacing: .04em; }
  .nav-cap { padding: 8px 6px 6px; }
  .nav-cap-txt { display: none; }
  .nav-cap::after { content: ''; display: block; height: 1px; background: var(--border); }
  .nav-group:first-child .nav-cap { display: none; }
  .nav-link { flex-direction: column; gap: 3px; min-height: 58px; padding: 8px 2px; }
  .nav-lbl { font-size: 10px; max-width: 100%; text-align: center; }
  .nav-lbl.full { display: none; }
  .nav-lbl.short { display: block; }
  .rail-btn { justify-content: center; }
  .rail-btn :deep(.lbl) { display: none; }
  .who { flex-direction: column; gap: 4px; min-height: 62px; padding: 8px 4px; }
  .who-txt { display: none; }
}

@media (max-width: 640px) {
  .shell { --pad: 8px; gap: 8px; }
  .company { display: none; }
}

.setup-link { display: flex; align-items: center; gap: 10px; color: var(--t2); }
.setup-link:hover { background: var(--surface-2); color: var(--t1); }
.setup-link.router-link-active { color: var(--accent); }
@media (max-width: 900px) {
  .setup-link { justify-content: center; }
  .setup-link .lbl { display: none; }
}
/* Полоса «только просмотр» стоит над экраном и отделена от него тем же
   зазором, что разделяет карточки. Пустой обёртка не занимает места:
   сама полоса рисуется только на узком экране. */
.ro-global:not(:empty) { margin-bottom: var(--gap, 12px); }
</style>

